<template>
  <div>
    <!-- Filters bar -->
    <div class="hr-bar">
      <!-- Week picker -->
      <div class="hr-field">
        <label>Week</label>
        <div class="hr-week-picker">
          <button class="hr-btn hr-btn-sm" @click="prevWeek">
            <b-icon icon="chevron-left" font-scale="0.85" />
          </button>
          <div
            class="hr-btn"
            style="min-width: 200px; justify-content: center; cursor: default"
          >
            <b-icon icon="calendar3" font-scale="0.8" />
            {{ weekLabel }}
          </div>
          <button class="hr-btn hr-btn-sm" @click="nextWeek">
            <b-icon icon="chevron-right" font-scale="0.85" />
          </button>
        </div>
      </div>

      <!-- Correction type -->
      <div class="hr-field">
        <label>Correction type</label>
        <select class="hr-select" v-model="typeFilter">
          <option value="all">All corrections</option>
          <option value="manual">Manual only</option>
          <option value="auto">Auto-rounded only</option>
        </select>
      </div>

      <!-- EA filter (admin) -->
      <div class="hr-field" v-if="role === 'admin'">
        <label>EA</label>
        <select class="hr-select" v-model="eaFilter" style="min-width: 160px">
          <option value="all">All EAs</option>
          <option
            v-for="ea in assistantOptions"
            :key="ea.value"
            :value="ea.value"
            >{{ ea.text }}</option
          >
        </select>
      </div>

      <!-- Export -->
      <div class="hr-bar-right" v-if="role === 'admin'">
        <button class="hr-btn hr-btn-primary" @click="exportCsv">
          <b-icon icon="download" font-scale="0.9" /> Export CSV
        </button>
      </div>
    </div>

    <!-- KPI cards -->
    <div class="hr-kpis">
      <div class="hr-card hr-kpi">
        <div class="k-top">
          <b-icon icon="pencil" font-scale="0.9" /> Manual corrections
        </div>
        <div class="k-val" style="color: #e11d48">{{ manualRows.length }}</div>
        <div class="k-sub">reason required</div>
      </div>
      <div class="hr-card hr-kpi">
        <div class="k-top">
          <b-icon icon="shield-check" font-scale="0.9" /> Auto-rounded
        </div>
        <div class="k-val">{{ autoRows.length }}</div>
        <div class="k-sub">system, nearest 5 min</div>
      </div>
      <div class="hr-card hr-kpi">
        <div class="k-top">
          <b-icon icon="graph-up" font-scale="0.9" /> Net manual adjustment
        </div>
        <div class="k-val">{{ netAdj >= 0 ? "+" : "" }}{{ netAdj }}m</div>
        <div class="k-sub">across manual edits</div>
      </div>
      <div class="hr-card hr-kpi">
        <div class="k-top">
          <b-icon icon="info-circle" font-scale="0.9" /> Top reason
        </div>
        <div class="k-val">{{ topReasonCount }}</div>
        <div class="k-sub">{{ topReasonText }}</div>
      </div>
    </div>

    <!-- Corrections table -->
    <div class="hr-card">
      <table class="hr-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Client</th>
            <th>Task</th>
            <th>Week</th>
            <th>Started by</th>
            <th>Corrected by</th>
            <th style="text-align: right">Original</th>
            <th style="text-align: right">New</th>
            <th>Reason</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredRows.length === 0">
            <td colspan="10">
              <div class="hr-empty">
                <div class="e-ic">
                  <b-icon icon="shield-check" font-scale="1.2" />
                </div>
                <div
                  style="font-weight: 700; color: #0f172a; margin-bottom: 4px"
                >
                  No corrections this week
                </div>
                <div style="font-size: 13px">
                  Every timer ran clean, or your filters exclude them all.
                </div>
              </div>
            </td>
          </tr>
          <tr v-for="(row, i) in filteredRows" :key="i">
            <td>
              <span v-if="row.kind === 'auto'" class="hr-badge b-slate"
                >Auto</span
              >
              <span v-else class="hr-badge b-amber">Manual</span>
            </td>
            <td style="font-weight: 600">{{ row.client }}</td>
            <td>{{ row.task }}</td>
            <td class="num" style="color: #64748b">W{{ weekNumber }}</td>
            <td>
              <div style="display: flex; gap: 8px; align-items: center">
                <span
                  class="hr-avatar"
                  :style="{ background: row.startedByColor }"
                  >{{ row.startedByInitials }}</span
                >
                <span style="font-size: 12.5px">{{ row.startedByName }}</span>
              </div>
            </td>
            <td>
              <span
                v-if="row.correctedBy === 'system'"
                style="display: flex; gap: 7px; align-items: center; color: #64748b; font-size: 12.5px"
              >
                <b-icon icon="shield-check" font-scale="0.85" /> System
              </span>
              <div v-else style="display: flex; gap: 8px; align-items: center">
                <span
                  class="hr-avatar"
                  :style="{ background: row.correctedByColor }"
                  >{{ row.correctedByInitials }}</span
                >
                <span style="font-size: 12.5px">{{ row.correctedByName }}</span>
              </div>
            </td>
            <td class="num" style="text-align: right; color: #64748b">
              {{ fmtH(row.from) }}
            </td>
            <td class="num" style="text-align: right; font-weight: 700">
              {{ fmtH(row.to) }}
            </td>
            <td style="font-size: 12.5px; color: #475569; max-width: 200px">
              {{ row.reason }}
            </td>
            <td style="text-align: right">
              <button
                v-if="correctionWindowOpen"
                class="hr-btn hr-btn-sm"
                @click="openCorrection(row)"
              >
                <b-icon icon="pencil" font-scale="0.75" /> Correct
              </button>
              <span
                v-else
                class="hr-badge b-slate"
                v-b-tooltip.hover
                :title="
                  isAdmin
                    ? 'This period is fully locked past the Wednesday deadline.'
                    : 'This period is locked. Only an Admin can correct it now.'
                "
                >Locked</span
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="hr-hint" style="margin-top: 12px">
      <b-icon icon="info-circle" font-scale="0.75" />
      Manual corrections require a reason and must land on a multiple of
      {{ minCorrectionMinutes }} minutes.
      <template v-if="!isAdmin">
        Corrections for a period are only allowed until the following Monday
        12:00 PM UTC.
      </template>
      <template v-else>
        As an Admin, corrections are allowed until the following Wednesday
        12:00 PM UTC.
      </template>
    </div>

    <!-- Correction Modal -->
    <div class="hr-overlay" v-if="editing" @click="closeModal">
      <div class="hr-modal" @click.stop>
        <div class="hr-modal-head">
          <span
            style="
              width: 34px; height: 34px; border-radius: 9px;
              background: #fce7ef; display: flex; align-items: center;
              justify-content: center;
            "
          >
            <b-icon icon="stopwatch" font-scale="1" style="color: #e11d48" />
          </span>
          <div>
            <div style="font-weight: 700">Correct timer</div>
            <div style="font-size: 12px; color: #94a3b8">
              {{ editing.task }} · {{ editing.client }}
            </div>
          </div>
          <button
            class="hr-btn hr-btn-sm"
            style="margin-left: auto; border: 0"
            @click="closeModal"
          >
            <b-icon icon="x" font-scale="1.1" />
          </button>
        </div>

        <div class="hr-modal-body">
          <div style="display: flex; gap: 22px; margin-bottom: 18px">
            <div>
              <div style="font-size: 11px; color: #94a3b8; font-weight: 600">
                STARTED BY
              </div>
              <div
                style="display: flex; gap: 8px; align-items: center; margin-top: 6px"
              >
                <span
                  class="hr-avatar"
                  :style="{ background: editing.startedByColor }"
                  >{{ editing.startedByInitials }}</span
                >
                {{ editing.startedByName }}
              </div>
            </div>
            <div>
              <div style="font-size: 11px; color: #94a3b8; font-weight: 600">
                RECORDED
              </div>
              <div class="num" style="margin-top: 8px; font-weight: 600">
                {{ fmtH(editing.from) }}
              </div>
            </div>
          </div>

          <div
            style="font-size: 12px; font-weight: 600; color: #64748b; margin-bottom: 8px"
          >
            New duration
          </div>
          <div style="display: flex; align-items: center; gap: 14px">
            <div class="hr-stepper">
              <button @click="stepModal(-5)">
                <b-icon icon="dash" font-scale="1.1" />
              </button>
              <div class="val">
                <input
                  type="text"
                  inputmode="numeric"
                  placeholder="h:mm"
                  class="val-input"
                  :value="fmtH(modalVal)"
                  @change="onDurationInput"
                /><span class="unit">h:mm</span>
              </div>
              <button @click="stepModal(5)">
                <b-icon icon="plus" font-scale="1.1" />
              </button>
            </div>
            <span
              v-if="modalDelta !== 0"
              :class="['hr-badge', modalDelta > 0 ? 'b-teal' : 'b-amber']"
              >{{ modalDelta > 0 ? "+" : "" }}{{ modalDelta }} min</span
            >
          </div>

          <div
            style="font-size: 12px; font-weight: 600; color: #64748b; margin: 18px 0 8px"
          >
            Reason for correction <span style="color: #e11d48">*</span>
          </div>
          <select class="hr-select" style="width: 100%" v-model="modalReason">
            <option value="">Select a reason…</option>
            <option v-for="r in reasons" :key="r">{{ r }}</option>
          </select>

          <div class="hr-confirm">
            <b-icon
              icon="exclamation-triangle"
              font-scale="0.9"
              style="flex-shrink: 0; margin-top: 1px"
            />
            <span>
              You're changing this timer from <b>{{ fmtH(editing.from) }}</b>
              to <b>{{ fmtH(modalVal) }}</b>. This affects billing. Are you
              sure?
            </span>
          </div>
        </div>

        <div class="hr-modal-foot">
          <button class="hr-btn" @click="closeModal">Cancel</button>
          <span v-b-tooltip.hover :title="confirmDisabledReason">
            <button
              class="hr-btn hr-btn-danger"
              :disabled="!modalReason || !modalDeltaValid"
              @click="saveCorrection"
            >
              <b-icon icon="check" font-scale="0.9" /> Confirm correction
            </button>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { getAssistantsList } from "@/apis/assistants";
import { getReportTimerCorrections } from "@/apis/customers";
import { patchTracker } from "@/apis/common";
import { mapState } from "vuex";
import {
  TIMER_CORRECTION_REASONS,
  ADMIN_INVOICE_CORRECTION_REASON,
  MIN_CORRECTION_MINUTES
} from "@/utils/constants/index";
import { snapCorrectionMinutes } from "@/utils/utils";

export default {
  name: "TimerCorrections",
  data() {
    return {
      weekStart: moment().startOf("isoWeek"),
      typeFilter: "all",
      eaFilter: "all",
      assistantOptions: [],
      manualRows: [],
      autoRows: [],
      netAdj: 0,
      topReasonCount: 0,
      topReasonText: "—",
      editing: null,
      modalVal: 0,
      modalReason: "",
      isLoading: false
    };
  },
  computed: {
    ...mapState("authModule", ["role", "profileUserId"]),

    isAdmin() {
      return this.role === "admin";
    },
    reasons() {
      return this.isAdmin
        ? [...TIMER_CORRECTION_REASONS, ADMIN_INVOICE_CORRECTION_REASON]
        : TIMER_CORRECTION_REASONS;
    },
    // EAs can correct the previous reporting period only until the
    // following Monday 12:00 PM UTC; admins are never locked out.
    correctionWindowOpen() {
      // EAs: locked after the following Monday 12:00 PM UTC.
      // Admins: extended until the following Wednesday 12:00 PM UTC.
      const deadlineOffsetDays = this.isAdmin ? 9 : 7;
      const deadline = moment
        .utc(this.weekStart)
        .startOf("day")
        .add(deadlineOffsetDays, "days")
        .add(12, "hours");
      return moment.utc().isSameOrBefore(deadline);
    },
    weekEnd() {
      return moment(this.weekStart).endOf("isoWeek");
    },
    weekNumber() {
      return this.weekStart.isoWeek();
    },
    weekLabel() {
      const ws = this.weekStart;
      const we = this.weekEnd;
      return `Week ${ws.isoWeek()} · ${ws.format("D")}–${we.format(
        "D MMM YYYY"
      )}`;
    },
    allRows() {
      return [...this.manualRows, ...this.autoRows].sort((a, b) =>
        a.kind === b.kind ? 0 : a.kind === "manual" ? -1 : 1
      );
    },
    filteredRows() {
      return this.allRows;
    },
    modalDelta() {
      return this.editing ? this.modalVal - this.editing.from : 0;
    },
    // modalVal is always kept on the 5-minute grid (see onDurationInput/
    // stepModal), so the only remaining invalid state is "nothing changed".
    modalDeltaValid() {
      return this.modalDelta !== 0;
    },
    minCorrectionMinutes() {
      return MIN_CORRECTION_MINUTES;
    },
    confirmDisabledReason() {
      if (!this.modalReason) return "Select a reason for this correction first";
      if (!this.modalDeltaValid) return "Choose a new duration before confirming";
      return "";
    }
  },
  watch: {
    weekStart: "loadCorrections",
    typeFilter: "loadCorrections",
    eaFilter: "loadCorrections"
  },
  async mounted() {
    await this.loadAssistants();
    await this.loadCorrections();
  },
  methods: {
    // Minutes -> "H:MM" (matches DailyView's identical fmtH).
    fmtH(m) {
      return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
    },
    prevWeek() {
      this.weekStart = moment(this.weekStart).subtract(1, "week");
    },
    nextWeek() {
      this.weekStart = moment(this.weekStart).add(1, "week");
    },
    async loadCorrections() {
      this.isLoading = true;
      try {
        const params = {
          completed_gte: this.weekStart.format("YYYY-MM-DD"),
          completed_lte: this.weekEnd.format("YYYY-MM-DD"),
          type: this.typeFilter,
          assigned_to: this.eaFilter
        };
        const res = await getReportTimerCorrections(params);
        const results = res.data.results || [];
        this.manualRows = results.filter(r => r.kind === "manual");
        this.autoRows = results.filter(r => r.kind === "auto");
        this.netAdj = res.data.netAdjustment || 0;
        this.topReasonCount = res.data.topReasonCount || 0;
        this.topReasonText = res.data.topReasonText || "—";
      } catch (err) {
        console.error("Error loading timer corrections:", err);
      } finally {
        this.isLoading = false;
      }
    },
    async loadAssistants() {
      try {
        const res = await getAssistantsList();
        this.assistantOptions = res.data.results.map(item => ({
          value: item.id,
          text: `${item.user.firstName} ${item.user.lastName}`
        }));
      } catch (e) {
        console.log(e);
      }
    },
    openCorrection(row) {
      // row.from is a historical audit value (what it was *before* the
      // last correction), not the tracker's current duration - basing the
      // modal off it produced wrong "RECORDED" values and invalid deltas
      // when correcting an already-corrected timer. The current duration is
      // row.to for manual rows (already-corrected) and row.from for auto
      // rows (nothing applied yet), rounded since auto shows raw un-rounded
      // minutes (e.g. 0.6m) which the stepper must start clean from.
      const currentMinutes =
        row.kind === "manual" ? row.to : Math.round(row.from);
      this.editing = { ...row, from: currentMinutes };
      this.modalVal = currentMinutes;
      this.modalReason = "";
    },
    // "1:30" / "90" / "" -> total minutes. Anything unparseable falls back
    // to the current value instead of silently zeroing the field out.
    parseHHMM(str) {
      const parts = String(str).trim().split(":");
      if (parts.length === 2) {
        const h = parseInt(parts[0], 10) || 0;
        const m = parseInt(parts[1], 10) || 0;
        return h * 60 + m;
      }
      const n = parseInt(str, 10);
      return isNaN(n) ? this.modalVal : n;
    },
    // The corrected total must land on a multiple of 5 minutes (the backend
    // enforces this) - snap on typed input, and on every +/- stepper click
    // too, so a non-multiple recording still lands clean instead of just
    // drifting by the raw step size.
    onDurationInput(e) {
      this.modalVal = snapCorrectionMinutes(
        this.parseHHMM(e.target.value),
        MIN_CORRECTION_MINUTES
      );
    },
    stepModal(delta) {
      this.modalVal = snapCorrectionMinutes(
        this.modalVal + delta,
        MIN_CORRECTION_MINUTES
      );
    },
    closeModal() {
      this.editing = null;
    },
    async saveCorrection() {
      if (!this.editing) return;
      this.isLoading = true;
      try {
        // Use .utc() before formatting - moment's [Z] format token is a
        // literal, not a real UTC conversion, so without .utc() this sends
        // local-time values mislabeled as UTC (silently shifting the time
        // by the browser's UTC offset).
        const stopAt = moment
          .utc(this.editing.startAt)
          .add(this.modalVal, "minutes")
          .format("YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]");

        const payload = {
          action: "correction",
          stopAt: stopAt,
          reason: this.modalReason
        };

        await patchTracker(this.editing.taskId, this.editing.id, payload);
        this.editing = null;
        this.$emit("notify", "Timer corrected");
        await this.loadCorrections();
      } catch (err) {
        console.error("Error saving timer correction:", err);
        this.$emit(
          "notify",
          "Error: " + (err.response?.data?.detail || err.message)
        );
      } finally {
        this.isLoading = false;
      }
    },
    exportCsv() {
      const head = [
        "Type",
        "Client",
        "Task",
        "Week",
        "Started by",
        "Corrected by",
        "Original (min)",
        "New (min)",
        "Reason"
      ];
      const rows = this.allRows.map(r => [
        r.kind === "auto" ? "Auto-round" : "Manual",
        r.client,
        r.task,
        `W${this.weekNumber}`,
        r.startedByName,
        r.correctedBy === "system" ? "System" : r.correctedByName,
        r.from,
        r.to,
        r.reason
      ]);
      const csv = [head, ...rows]
        .map(r =>
          r
            .map(c => {
              const v = String(c ?? "");
              return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
            })
            .join(",")
        )
        .join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `headroom_timer_corrections_w${this.weekNumber}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      this.$emit("notify", "CSV downloaded");
    }
  }
};
</script>
